import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Settings, Wifi, WifiOff, RefreshCw, CheckCircle, XCircle,
  AlertTriangle, Loader2, Eye, EyeOff, ExternalLink, Zap, Globe,
  Activity, Key } from
'lucide-react';


export default function AdConnectors() {const { t } = useTranslation("crm");
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [connectorConfigs, setConnectorConfigs] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [testingConnector, setTestingConnector] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, configsRes, healthRes] = await Promise.all([
      fetch(`${API_BASE}/api/ad-connectors/types`),
      fetch(`${API_BASE}/api/ad-connectors/configs`),
      fetch(`${API_BASE}/api/ad-connectors/health`)]
      );

      const typesData = await typesRes.json();
      const configsData = await configsRes.json();
      const healthData = await healthRes.json();

      setConnectorTypes(Array.isArray(typesData) ? typesData : []);
      setConnectorConfigs(Array.isArray(configsData) ? configsData : []);
      setHealthData(healthData);
    } catch (error) {
      console.error('Failed to fetch connectors:', error);
      toast.error('Failed to load connector data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestConnector = async (configId) => {
    setTestingConnector(configId);
    try {
      const res = await fetch(`${API_BASE}/api/ad-connectors/configs/${configId}/test`, { method: 'POST' });
      const data = await res.json();

      if (data.success) {
        toast.success(`Connected successfully! Found ${data.accounts?.length || 0} ad accounts.`);
      } else {
        toast.error(data.error || 'Connection test failed');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to test connector');
    }
    setTestingConnector(null);
  };

  const handleSaveConfig = async (formData) => {
    try {
      const url = selectedConfig ?
      `${API_BASE}/api/ad-connectors/configs/${selectedConfig.id}` :
      `${API_BASE}/api/ad-connectors/configs`;

      const response = await fetch(url, {
        method: selectedConfig ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save connector');

      toast.success(selectedConfig ? 'Connector updated' : 'Connector created');
      setShowConfigModal(false);
      setShowAddModal(false);
      setSelectedConfig(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStateIcon = (state) => {
    const icons = {
      active: <Wifi className="w-5 h-5 text-green-500" />,
      disabled: <WifiOff className="w-5 h-5 text-gray-400" />,
      needs_human_action: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
      rate_limited: <AlertTriangle className="w-5 h-5 text-orange-500" />,
      auth_expired: <XCircle className="w-5 h-5 text-red-500" />,
      error: <XCircle className="w-5 h-5 text-red-500" />
    };
    return icons[state] || icons.disabled;
  };

  const getStateBadge = (state) => {
    const variants = {
      active: 'bg-green-100 text-green-700 border-green-200',
      disabled: 'bg-gray-100 text-gray-500 border-gray-200',
      needs_human_action: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      rate_limited: 'bg-orange-100 text-orange-700 border-orange-200',
      auth_expired: 'bg-red-100 text-red-700 border-red-200',
      error: 'bg-red-100 text-red-700 border-red-200'
    };
    return (
      <Badge variant="outline" className={variants[state] || variants.disabled}>
        {state?.replace(/_/g, ' ')}
      </Badge>);

  };

  const getConnectorIcon = (type) => {
    switch (type) {
      case 'google_ads':
        return (
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-white font-bold text-sm">
            G
          </div>);

      case 'meta_ads':
        return (
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
            M
          </div>);

      case 'linkedin_ads':
        return (
          <div className="w-10 h-10 rounded-lg bg-blue-700 flex items-center justify-center text-white font-bold text-sm">{t("in", "in")}

          </div>);

      case 'tiktok_ads':
        return (
          <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center text-white font-bold text-sm">{t("tt", "TT")}

          </div>);

      default:
        return (
          <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
            <Globe className="w-5 h-5 text-gray-500" />
          </div>);

    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>);

  }

  return (
    <div className="p-6 space-y-6" data-testid="ad-connectors">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t("adNetworkConnectors", "Ad Network Connectors")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("connectToAdvertising", "Connect to advertising platforms for Mode A automated publishing")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-connector-btn">
            <Plus className="w-4 h-4 mr-2" />{t("addConnector", "Add Connector")}

          </Button>
        </div>
      </div>

      {/* Health Status */}
      {healthData &&
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
      healthData.status === 'healthy' ? 'bg-green-50 dark:bg-green-900/20' :
      healthData.status === 'degraded' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
      'bg-red-50 dark:bg-red-900/20'}`
      }>
          <Activity className={`w-5 h-5 ${
        healthData.status === 'healthy' ? 'text-green-500' :
        healthData.status === 'degraded' ? 'text-yellow-500' :
        'text-red-500'}`
        } />
          <span className="text-sm font-medium">{t("overallStatus", "Overall Status:")}
          <span className="capitalize">{healthData.status}</span>
          </span>
          <span className="text-sm text-gray-500">
            • {healthData.connectors?.filter((c) => c.status === 'healthy').length || 0}{t("healthy", "healthy\n            \u2022")}
          {healthData.connectors?.filter((c) => c.status === 'degraded').length || 0}{t("degraded", "degraded\n            \u2022")}
          {healthData.connectors?.filter((c) => c.status === 'unhealthy').length || 0}{t("unhealthy", "unhealthy")}
        </span>
        </div>
      }

      {/* Available Connector Types */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("availablePlatforms", "Available Platforms")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {connectorTypes.map((type) =>
          <Card key={type.type} className={`hover:shadow-lg transition-shadow ${type.status === 'coming_soon' ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {getConnectorIcon(type.type)}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{type.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                    {type.status === 'coming_soon' &&
                  <Badge variant="secondary" className="mt-2">{t("comingSoon", "Coming Soon")}</Badge>
                  }
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Configured Connectors */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("configuredConnectors", "Configured Connectors")}</h2>
        {connectorConfigs.length === 0 ?
        <Card className="bg-gray-50 dark:bg-gray-800/50">
            <CardContent className="p-12 text-center">
              <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">{t("noConnectorsConfigured", "No connectors configured yet")}</p>
              <p className="text-sm text-gray-400 mt-1">{t("addAConnector", "Add a connector to enable Mode A automated publishing")}</p>
              <Button className="mt-4" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />{t("addYourFirst", "Add Your First Connector")}

            </Button>
            </CardContent>
          </Card> :

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {connectorConfigs.map((config) =>
          <Card key={config.id} className="hover:shadow-lg transition-shadow" data-testid={`connector-${config.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {getConnectorIcon(config.connector_type)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{config.name}</h3>
                        {getStateBadge(config.state)}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 capitalize">{config.connector_type?.replace(/_/g, ' ')}</p>
                      
                      {/* Account Info */}
                      {config.account_ids && config.account_ids.length > 0 &&
                  <div className="mt-3 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {config.account_ids.length}{t("account", "account")}{config.account_ids.length > 1 ? 's' : ''}{t("connected", "connected")}
                    </Badge>
                        </div>
                  }
                      
                      {/* Rate Limit Warning */}
                      {config.rate_limit_remaining && config.rate_limit_remaining < 100 &&
                  <div className="mt-2 text-xs text-orange-600">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />{t("rateLimit", "Rate limit:")}
                    {config.rate_limit_remaining}{t("remaining", "% remaining")}
                  </div>
                  }
                      
                      {/* Last Sync */}
                      {config.last_sync_at &&
                  <p className="text-xs text-gray-400 mt-2">{t("lastSynced", "Last synced:")}
                    {new Date(config.last_sync_at).toLocaleString()}
                        </p>
                  }
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestConnector(config.id)}
                      disabled={testingConnector === config.id}>
                      
                          {testingConnector === config.id ?
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" /> :

                      <Activity className="w-3 h-3 mr-1" />
                      }{t("test", "Test")}

                    </Button>
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {setSelectedConfig(config);setShowConfigModal(true);}}>
                      
                          <Settings className="w-3 h-3 mr-1" />{t("configure", "Configure")}

                    </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
          )}
          </div>
        }
      </div>

      {/* Add Connector Modal */}
      <AddConnectorModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        connectorTypes={connectorTypes.filter((t) => t.status !== 'coming_soon')}
        onSave={handleSaveConfig} />
      

      {/* Configure Connector Modal */}
      <ConfigureConnectorModal
        open={showConfigModal}
        onClose={() => {setShowConfigModal(false);setSelectedConfig(null);}}
        config={selectedConfig}
        connectorTypes={connectorTypes}
        onSave={handleSaveConfig} />
      
    </div>);

}

// Add Connector Modal
function AddConnectorModal({ open, onClose, connectorTypes, onSave }) {const { t } = useTranslation("crm");
  const [selectedType, setSelectedType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    client_secret: '',
    developer_token: '',
    access_token: '',
    refresh_token: ''
  });
  const [showSecrets, setShowSecrets] = useState(false);

  const selectedConnector = connectorTypes.find((t) => t.type === selectedType);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      connector_type: selectedType,
      ...formData
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("addAdNetwork", "Add Ad Network Connector")}</DialogTitle>
          <DialogDescription>{t("connectToAn", "Connect to an advertising platform for automated campaign publishing.")}

          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t("platform", "Platform *")}</Label>
            <Select value={selectedType} onValueChange={setSelectedType} required>
              <SelectTrigger>
                <SelectValue placeholder="Select platform" />
              </SelectTrigger>
              <SelectContent>
                {connectorTypes.map((type) =>
                <SelectItem key={type.type} value={type.type}>{type.name}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedType &&
          <>
              <div className="space-y-2">
                <Label>{t("connectionName", "Connection Name *")}</Label>
                <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={`My ${selectedConnector?.name || 'Connector'}`}
                required />
              
              </div>

              {/* Credentials Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{t("apiCredentials", "API Credentials")}</Label>
                  <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSecrets(!showSecrets)}>
                  
                    {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>

                {selectedConnector?.requires?.includes('client_id') &&
              <div className="space-y-2">
                    <Label className="text-xs">{t("clientId", "Client ID")}</Label>
                    <Input
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  placeholder="Enter Client ID" />
                
                  </div>
              }

                {selectedConnector?.requires?.includes('client_secret') &&
              <div className="space-y-2">
                    <Label className="text-xs">{t("clientSecret", "Client Secret")}</Label>
                    <Input
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.client_secret}
                  onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                  placeholder="Enter Client Secret" />
                
                  </div>
              }

                {selectedConnector?.requires?.includes('developer_token') &&
              <div className="space-y-2">
                    <Label className="text-xs">{t("developerTokenGoogle", "Developer Token (Google Ads)")}</Label>
                    <Input
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.developer_token}
                  onChange={(e) => setFormData({ ...formData, developer_token: e.target.value })}
                  placeholder="Enter Developer Token" />
                
                  </div>
              }

                {selectedConnector?.requires?.includes('access_token') &&
              <div className="space-y-2">
                    <Label className="text-xs">{t("accessToken", "Access Token")}</Label>
                    <Input
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.access_token}
                  onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                  placeholder="Enter Access Token" />
                
                  </div>
              }

                {selectedConnector?.requires?.includes('refresh_token') &&
              <div className="space-y-2">
                    <Label className="text-xs">{t("refreshToken", "Refresh Token")}</Label>
                    <Input
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.refresh_token}
                  onChange={(e) => setFormData({ ...formData, refresh_token: e.target.value })}
                  placeholder="Enter Refresh Token" />
                
                  </div>
              }
              </div>

              {/* OAuth Scopes Info */}
              {selectedConnector?.oauth_scopes?.length > 0 &&
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium mb-1">{t("requiredOauthScopes", "Required OAuth Scopes:")}</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedConnector.oauth_scopes.map((scope, i) =>
                <Badge key={i} variant="outline" className="text-xs font-mono">{scope}</Badge>
                )}
                  </div>
                </div>
            }
            </>
          }

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}</Button>
            <Button type="submit" disabled={!selectedType}>
              <Key className="w-4 h-4 mr-2" />{t("addConnector", "Add Connector")}

            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>);

}

// Configure Connector Modal
function ConfigureConnectorModal({ open, onClose, config, connectorTypes, onSave }) {const { t } = useTranslation("crm");
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    client_secret: '',
    developer_token: '',
    access_token: '',
    refresh_token: '',
    state: 'disabled'
  });
  const [showSecrets, setShowSecrets] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        name: config.name || '',
        client_id: config.client_id || '',
        client_secret: '',
        developer_token: '',
        access_token: '',
        refresh_token: '',
        state: config.state || 'disabled'
      });
    }
  }, [config, open]);

  const connectorType = connectorTypes.find((t) => t.type === config?.connector_type);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updates = { name: formData.name, state: formData.state };
    if (formData.client_id) updates.client_id = formData.client_id;
    if (formData.client_secret) updates.client_secret = formData.client_secret;
    if (formData.developer_token) updates.developer_token = formData.developer_token;
    if (formData.access_token) updates.access_token = formData.access_token;
    if (formData.refresh_token) updates.refresh_token = formData.refresh_token;
    onSave(updates);
  };

  if (!config) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("configure", "Configure")}{config.name}</DialogTitle>
          <DialogDescription>{t("updateConnectorCredentials", "Update connector credentials and settings.")}

          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t("connectionName", "Connection Name")}</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required />
            
          </div>

          <div className="space-y-2">
            <Label>{t("status", "Status")}</Label>
            <Select value={formData.state} onValueChange={(v) => setFormData({ ...formData, state: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t("active", "Active")}</SelectItem>
                <SelectItem value="disabled">{t("disabled", "Disabled")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Update Credentials */}
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{t("updateCredentials", "Update Credentials")}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowSecrets(!showSecrets)}>
                
                {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500">{t("leaveBlankTo", "Leave blank to keep existing values")}</p>

            <div className="space-y-2">
              <Label className="text-xs">{t("clientId", "Client ID")}</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                placeholder={config.has_client_id ? '••••••••' : 'Enter Client ID'} />
              
            </div>

            <div className="space-y-2">
              <Label className="text-xs">{t("clientSecret", "Client Secret")}</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                value={formData.client_secret}
                onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                placeholder={config.has_client_secret ? '••••••••' : 'Enter Client Secret'} />
              
            </div>

            {config.connector_type === 'google_ads' &&
            <div className="space-y-2">
                <Label className="text-xs">{t("developerToken", "Developer Token")}</Label>
                <Input
                type={showSecrets ? 'text' : 'password'}
                value={formData.developer_token}
                onChange={(e) => setFormData({ ...formData, developer_token: e.target.value })}
                placeholder={config.has_developer_token ? '••••••••' : 'Enter Developer Token'} />
              
              </div>
            }

            <div className="space-y-2">
              <Label className="text-xs">{t("accessToken", "Access Token")}</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                value={formData.access_token}
                onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                placeholder={config.has_access_token ? '••••••••' : 'Enter Access Token'} />
              
            </div>

            <div className="space-y-2">
              <Label className="text-xs">{t("refreshToken", "Refresh Token")}</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                value={formData.refresh_token}
                onChange={(e) => setFormData({ ...formData, refresh_token: e.target.value })}
                placeholder={config.has_refresh_token ? '••••••••' : 'Enter Refresh Token'} />
              
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}</Button>
            <Button type="submit">{t("saveChanges", "Save Changes")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>);

}
